/**
 * Composable for the v2 migration promo popup.
 *
 * Behavior:
 * - Visible until `v2MigrationShutdownAt` (env-config).
 * - Countdown to shutdown shown for the whole window.
 * - Throttled to one show per UTC calendar day.
 */

import useEnvs from '~/composables/useEnvs';

const STORAGE_KEY = 'v2-migration-last-shown';

interface V2MigrationState {
  shouldShow: ComputedRef<boolean>;
  shutdownDate: Date;
  v2GameUrl: string;
  markAsShown: () => void;
}

function getUTCDateKey(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export default function useV2Migration(): V2MigrationState {
  if (import.meta.server) {
    return {
      shouldShow: computed(() => false),
      shutdownDate: new Date(0),
      v2GameUrl: '',
      markAsShown: () => {}
    };
  }

  const envs = useEnvs();
  const shutdownDate = new Date(envs.v2MigrationShutdownAt);
  const v2GameUrl = envs.v2GameUrl || '';

  const shouldShow = ref(false);
  // Sticky flag: once the user dismisses or sees the popup in this page session,
  // the hourly re-check must not re-pop it — even if UTC midnight rolls over.
  let shownInThisSession = false;

  const checkStatus = () => {
    if (shownInThisSession) {
      shouldShow.value = false;
      return;
    }

    const shutdown = shutdownDate.getTime();

    if (Number.isNaN(shutdown) || Date.now() >= shutdown) {
      shouldShow.value = false;
      return;
    }

    const todayKey = getUTCDateKey(new Date());
    const lastShown = localStorage.getItem(STORAGE_KEY);
    shouldShow.value = lastShown !== todayKey;
  };

  let interval: ReturnType<typeof setInterval> | null = null;
  onMounted(() => {
    checkStatus();
    interval = setInterval(checkStatus, 60 * 60 * 1000);
  });
  onBeforeUnmount(() => {
    if (interval) {
      clearInterval(interval);
      interval = null;
    }
  });

  const markAsShown = () => {
    localStorage.setItem(STORAGE_KEY, getUTCDateKey(new Date()));
    shownInThisSession = true;
    shouldShow.value = false;
  };

  return {
    shouldShow: computed(() => shouldShow.value),
    shutdownDate,
    v2GameUrl,
    markAsShown
  };
}
